import { useContext, useState } from "react";
import { AuthContext, AuthUtils } from "@bfc-software/app-toolbox";
import { ComboBox } from "@bfc-software/pallet";
import type { TFunction } from "i18next";

import { selectorParam, useTaskFilters } from "../-data/filters";
import { filterSelectorOptions } from "../-data/selector-options";
import { useSelectorRoster } from "../-data/use-selector-roster";

import styles from "./selector-filter.module.css";

/**
 * The selector filter, shared by both tabs.
 *
 * It sits in the tab bar rather than inside either tab, for the same reason
 * the two search boxes sit one level up in `_layout.tsx`: `selector` is a URL
 * param both tabs read through `useTaskFilters`, and a control that lived
 * inside one tab would stop narrowing the moment the other tab was open.
 *
 * Every option is a person, and picking one reaches the wire on both tabs as
 * the same `selectorAppUserExtIds` param: the activation listing narrows to
 * that person's rows, and the plan listing to the plans holding a live
 * assignment to them. The box used to carry an "unassigned" option as well,
 * which no plan endpoint could answer for - the tasks nobody holds are a task
 * status, and the All tasks tab's `Unassigned` chip is where that question is
 * asked now.
 *
 * The typed text and whether the dropdown is open are both held here in
 * `useState` rather than in the URL: neither is a filter in its own right, only
 * the means of picking one, so neither is something a manager would want to
 * send to someone else. What reaches the URL is the picked option's id, exactly
 * as `filters.ts` expects it back - and the roster is what turns that id back
 * into a name, which is why it is read on mount rather than on first open.
 */
export function SelectorFilter({
  params,
  t,
}: {
  params: { org: string; location: string };
  t: TFunction;
}) {
  const { state: authState } = useContext(AuthContext);
  const orgId = AuthUtils.getOrgIdByStub(authState, params.org);
  const warehouseId =
    typeof orgId === "string"
      ? (AuthUtils.getWarehouseId(authState, orgId, params.location) ??
        undefined)
      : undefined;

  const { filters, setSearch } = useTaskFilters();

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const roster = useSelectorRoster({ orgId, warehouseId });
  const rosterOptions = roster.data ?? [];

  const filteredOptions = filterSelectorOptions(rosterOptions, query);

  /**
   * What the box shows when it is not the thing someone is mid-keystroke on:
   * read off the ADT rather than off the raw `selector` param, so a `named`
   * filter shows the roster's own label rather than the bare AAA id. The
   * roster is read on mount, so that is a first-paint flicker at worst rather
   * than the id standing until somebody opens the box - and the id is what the
   * task table's own Selector column draws anyway, so even the flicker does not
   * look unresolved.
   */
  const displayValue = ((): string => {
    switch (filters.selector.kind) {
      case "any":
        return "";
      case "named": {
        // Read into a local first: narrowing does not survive into the
        // `find` callback below.
        const name = filters.selector.name;
        const known = rosterOptions.find((option) => option.id === name);
        return known?.label ?? name;
      }
    }
  })();

  const selectedId = selectorParam(filters.selector) ?? null;

  return (
    <ComboBox
      className={styles.selectorFilter}
      aria-label={t("tasks.selectorFilter.ariaLabel")}
      placeholder={t("tasks.selectorFilter.placeholder")}
      items={filteredOptions}
      inputValue={isOpen ? query : displayValue}
      onInputChange={setQuery}
      selectedId={selectedId}
      onSelectionChange={(id) => setSearch({ selector: id ?? undefined })}
      onClear={() => {
        setSearch({ selector: undefined });
        setQuery("");
      }}
      clearLabel={t("tasks.selectorFilter.clear")}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) {
          // Opening onto the current selection rather than onto an empty box.
          // Whoever is opening a box that already names someone is usually
          // correcting it, and starting from the name means backspacing the
          // last few characters rather than retyping all of it - while a box
          // that blanked itself on open also hid what it was about to lose.
          // It costs the list being narrowed to one on open, which is the
          // right list for the value that is showing.
          setQuery(displayValue);
        }
      }}
      isLoading={roster.isPending}
      loadingLabel={t("tasks.selectorFilter.loading")}
      emptyLabel={t("tasks.selectorFilter.empty")}
      data-testid="selector-filter"
    />
  );
}
